import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import uniqid from 'uniqid';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import { DynamicDetailsModal } from '@Components/Control';
import { buildDateTimeLabel } from '@Helpers/util';
import { NoDataPlaceholder, renderTableHeader, TableWrapper } from '../../SharedComponents';
import { CLAIM_HISTORY_HEADER_MAP } from '../constants';

const ClaimHistoryTable = ({
  small,
  loading,
  historyData,
}) => {
  const { theme: { page } } = useAppTheme();
  const events = historyData?.event || [];

  const renderDataPoint = (uid, row) => {
    if (uid === 'time') {
      return (
        <Text size="1rem" color={page.primaryText}>
          {buildDateTimeLabel(row.time)}
        </Text>
      );
    }

    /**
     * This is private information that we don't want to show by default.
     * Hidden temporarily by WASIQ on 2026/02/20
     */
    // if (uid === 'source' && row.source) {
    //   return (
    //     <DynamicDetailsModal
    //       tableButtonStyle
    //       small={small}
    //       label="Source details"
    //       details={row.source}
    //       buttonHighlight={button.primaryBg}
    //       textColor={page.primaryText}
    //     />
    //   );
    // }

    if (uid === 'type') {
      if (row.typeDetails) {
        return (
          <DynamicDetailsModal
            tableButtonStyle
            small={small}
            label={row.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            details={row.typeDetails}
          />
        );
      }

      return (
        <Text size="1rem" color={page.tableText}>
          {row.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </Text>
      );
    }
    if (uid === 'reason') {
      return (
        <Text size="1rem" color={page.tableText}>
          {Case.title(row.reason) || '—'}
        </Text>
      );
    }
    return (
      <Text size="1rem" color={page.tableText}>
        {row[uid] || '—'}
      </Text>
    );
  };

  const renderTableContents = () => {
    if (loading || !events.length) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={false}
          loading={loading}
          label="Loading claim history..."
          noDataLabel="No claim history found."
          textColor={page.tableText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {CLAIM_HISTORY_HEADER_MAP.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={size}
                pad={{
                  horizontal: '0.5rem',
                  top: '1rem',
                  bottom: '0.5rem',
                }}
              >
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {events.map((row) => (
          <Box
            key={uniqid()}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {CLAIM_HISTORY_HEADER_MAP.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{
                  horizontal: small ? '0' : '0.5rem',
                  vertical: small ? '0.25rem' : '0',
                }}
                direction="row"
                gap="0.5rem"
              >
                {small && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderDataPoint(uid, row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <TableWrapper
        hidePagination
        small={small}
        boxPad={small ? '0.5rem' : '1rem'}
        header="Claim History"
        tableControls={null}
      >
        {renderTableContents()}
      </TableWrapper>
    </Box>
  );
};

ClaimHistoryTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  historyData: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.string,
    event: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.string,
      time: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      typeDetails: PropTypes.object,
      reason: PropTypes.string,
      source: PropTypes.object,
    })),
  }),
};

export default ClaimHistoryTable;
